import { injectable } from 'inversify-props';
import dayjs from '@/plugins/dayjs';
import BaseService from '@/services/base-service';
import AttendanceModel from '@/models/crm/attendance.model';
import AttendanceSummaryModel from '@/models/crm/attendance-summary.model';
import AttendanceInsightModel from '@/models/crm/attendance-insight.model';
import { RequestConfig } from '@/interfaces/request-config.interface';
import { ClientTypeEnum } from '../../enums/client-type.enum';
import ResponsibleAttendantModel from '@/models/crm/responsible-attendant.model';
import CRMPanelModel from '@/models/crm/panel.model';
import CRMPanelCreationModel from '@/models/crm/panel-creation.model';
import OrderModel from '@/models/crm/order.model';

@injectable()
export default class AttendanceService extends BaseService {
  async generate(
    clientId: string,
    prospectId: number | null,
    type: ClientTypeEnum,
    index: string,
    initialDate: string,
    finalDate: string,
  ): Promise<OrderModel[]> {
    const config: RequestConfig = {
      params: {
        clientId,
        prospectId,
        index,
        type,
        initialDate,
        finalDate,
      },
    };
    return (await this.httpService.get(
      OrderModel,
      `${this.getApiPath()}/crm/generate-attendance`,
      config,
    )) as OrderModel[];
  }

  async get(
    clientId: string,
    type: ClientTypeEnum,
    index: string,
    limit: number,
    offset = 0,
    initialDate: string,
    finalDate: string,
    origins?: string[],
    keyword?: string,
  ): Promise<AttendanceModel[]> {
    const config: RequestConfig = {
      params: {
        clientId,
        index,
        limit,
        offset,
        type,
        initialDate,
        finalDate,
        origins: origins && origins.length > 0 ? origins.join(',') : undefined,
        keyword: keyword && keyword.trim() ? keyword.trim() : undefined,
      },
    };
    return (await this.httpService.get(
      AttendanceModel,
      `${this.getApiPath()}/crm/attendance`,
      config,
    )) as AttendanceModel[];
  }

  async getSummary(clientId: string, type: ClientTypeEnum, index: string): Promise<AttendanceSummaryModel | null> {
    const config: RequestConfig = {
      params: { clientId, type, index },
    };
    return (await this.httpService.get(
      AttendanceSummaryModel,
      `${this.getApiPath()}/crm/attendance/summary`,
      config,
    )) as AttendanceSummaryModel;
  }

  // Insight existente vai por insightId; o ainda não gerado não tem id e vai pelo par cliente +
  // representante, que o backend só aceita se houver visita transcrita.
  async refreshInsight(
    insightId?: number,
    clientId?: string,
    codRepres?: string,
  ): Promise<AttendanceInsightModel> {
    const config: RequestConfig = {
      params: insightId ? { insightId } : { clientId, codRepres },
      // Sem isto herda o default do axios, que é esperar sem limite. Cobre uma tentativa do
      // AI Gateway (120s).
      timeout: 125000,
    };
    return (await this.httpService.post(
      AttendanceInsightModel,
      `${this.getApiPath()}/crm/attendance/insight/refresh`,
      undefined,
      config,
    )) as AttendanceInsightModel;
  }

  static generateAttendanceExportFilename(date: Date): string {
    return `Empresa_Atendimentos_${dayjs(date).format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
  }

  async getAttendantList(): Promise<ResponsibleAttendantModel[]> {
    return (await this.httpService.get(
      ResponsibleAttendantModel,
      `${this.getApiPath()}/attendant/list`,
    )) as ResponsibleAttendantModel[];
  }

  async setResponsibleAttendant(panelCreationData: CRMPanelCreationModel): Promise<CRMPanelModel> {
    return (await this.httpService.post(
      CRMPanelModel,
      `${this.getApiPath()}/attendant/upsert`,
      panelCreationData,
    )) as CRMPanelModel;
  }
}
